/**
 * Lobby recommendations query hook.
 *
 * recommendationsService.getRecommendations(isLoggedIn, opts) returns either
 * personalized (logged-in) or general (anonymous) recommendations - so this
 * query runs for everyone and is keyed by userId (undefined for anonymous,
 * which is a stable distinct cache entry from a logged-in player's).
 *
 * Launching a game changes future recommendations; the lobby invalidates this
 * key after a launch (see useGameLobby) rather than refetching imperatively.
 */

import { useQuery } from "@tanstack/react-query";
import recommendationsService from "../../../shared/services/recommendationsService";
import { queryKeys } from "../../../shared/data/queryKeys";
import { normalizeQueryError } from "../../../shared/data/errors";

/**
 * @param {boolean} isLoggedIn
 * @param {string|undefined} userId - keys the cache entry per player
 */
export function useRecommendations(isLoggedIn, userId) {
  return useQuery({
    queryKey: queryKeys.games.recommendations(userId),
    queryFn: async () => {
      try {
        const result = await recommendationsService.getRecommendations(
          isLoggedIn,
          {
            limit: 20,
            currency: "SS1", // SS1 in production per Hub88 operator config
            device: "desktop",
          },
        );
        return result.recommendations || [];
      } catch (err) {
        throw normalizeQueryError(err, "Failed to load recommendations");
      }
    },
  });
}

export default useRecommendations;
